import { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DatabaseZap, Loader2, AlertTriangle, CheckCircle2, DownloadCloud } from 'lucide-react';
import { toast } from 'sonner';

const COLLECTION_LABELS = {
  leads: 'CRM Partners & Contacts', tasks: 'Tasks', activities: 'Notes & Follow-Ups',
  task_comments: 'Task Activity Journals', task_events: 'Task Timelines', calendar: 'Calendar Events',
  meeting_types: 'Meeting Types', bookings: 'Meetings & Bookings', lead_documents: 'Documents',
  resources: 'Resources', goals: 'Goals', goal_achievements: 'Goal Achievements',
};

export default function DataSyncCard() {
  const [form, setForm] = useState({ source_url: 'https://aawta-hub.preview.emergentagent.com', email: '', password: '' });
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const run = async (execute) => {
    if (!form.source_url || !form.email || !form.password) return toast.error('Source URL and Leadership credentials required');
    if (execute && !window.confirm('Execute the data sync now? Records will be merged into THIS environment (newer wins, no duplicates). This cannot be undone.')) return;
    setBusy(execute ? 'exec' : 'dry');
    try {
      const r = await api.post('/admin/sync/run', { ...form, execute });
      setResult(r.data);
      toast.success(execute ? 'Synchronization complete' : 'Dry run complete — review the summary below');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setBusy('');
    }
  };

  const downloadBackup = async () => {
    setBusy('backup');
    try {
      const r = await api.get('/admin/backup/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aawta-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Full database backup downloaded');
    } catch (e) {
      toast.error('Backup failed');
    } finally { setBusy(''); }
  };

  const totals = result ? Object.values(result.report).reduce((a, v) => ({
    added: a.added + v.added, updated: a.updated + v.updated,
    kept: a.kept + v.kept_production, dups: a.dups + v.duplicates_matched,
  }), { added: 0, updated: 0, kept: 0, dups: 0 }) : null;

  return (
    <Card data-testid="data-sync-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><DatabaseZap className="w-5 h-5 text-magenta" /> Data Sync (Migration Utility)</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          One-time migration tool: pulls data FROM another environment (e.g. preview) INTO this one. Merges by record ID
          and smart duplicate matching — newest version wins, relationships preserved. User accounts, passwords, roles,
          OAuth credentials, settings, and audit history are never touched. Leadership only.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 space-y-1" data-testid="sync-safety-note">
          <strong>Data safety guarantees:</strong>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Sync NEVER deletes records from this environment.</li>
            <li>Newer data here is never overwritten by older source data.</li>
            <li>Updates are field-level — fields that exist only here are preserved.</li>
            <li>All notes, tasks, meetings, documents and history stay attached.</li>
          </ul>
          <Button variant="outline" size="sm" className="mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={downloadBackup} disabled={!!busy} data-testid="download-backup-btn">
            {busy === 'backup' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5 mr-1.5" />}
            Download Full Backup (do this before syncing)
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Source environment URL</Label>
            <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="sync-source-url" />
          </div>
          <div>
            <Label className="text-xs">Source Leadership email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sync-email" />
          </div>
          <div>
            <Label className="text-xs">Source password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sync-password" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => run(false)} disabled={!!busy} data-testid="sync-dry-run">
            {busy === 'dry' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Preview Sync (Dry Run)
          </Button>
          <Button onClick={() => run(true)} disabled={!!busy || !result || result.mode === 'executed'} className="bg-magenta hover:bg-magenta/90" data-testid="sync-execute">
            {busy === 'exec' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Execute Sync
          </Button>
        </div>
        {!result && <p className="text-[11px] text-slate-400">Run the dry run first — Execute unlocks after you review the summary.</p>}

        {result && (
          <div className="space-y-3" data-testid="sync-result">
            <div className={`rounded-xl border p-3 flex items-center gap-3 ${result.mode === 'executed' ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
              <CheckCircle2 className={`w-6 h-6 shrink-0 ${result.mode === 'executed' ? 'text-emerald-500' : 'text-blue-500'}`} />
              <div className="text-sm">
                <strong className="text-navy">{result.mode === 'executed' ? 'Synchronization complete' : 'Dry run — no changes made yet'}</strong>
                <div className="text-xs text-slate-500">
                  {totals.added} to add · {totals.updated} to update (newer) · {totals.kept} kept (production newer/identical) · {totals.dups} duplicates matched by content
                  {result.mode === 'executed' && result.files_queued > 0 && ` · ${result.files_queued} files transferring in background`}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-400 uppercase tracking-wider">
                  <th className="py-1 pr-2">Data</th><th className="py-1 pr-2">Before</th><th className="py-1 pr-2">After</th><th className="py-1 pr-2">Added</th><th className="py-1 pr-2">Updated</th><th className="py-1 pr-2">Kept</th><th className="py-1 pr-2">Merged dupes</th><th className="py-1">Deleted</th>
                </tr></thead>
                <tbody>
                  {Object.entries(result.report).map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2 font-medium text-navy">{COLLECTION_LABELS[k] || k}</td>
                      <td className="py-1.5 pr-2">{v.records_before ?? '—'}</td><td className="py-1.5 pr-2">{v.records_after ?? '—'}</td>
                      <td className="py-1.5 pr-2">{v.added}</td><td className="py-1.5 pr-2">{v.updated}</td>
                      <td className="py-1.5 pr-2">{v.kept_production}</td><td className="py-1.5 pr-2">{v.duplicates_matched}</td>
                      <td className="py-1.5 font-semibold text-emerald-600">{v.deleted ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.unmatched_users?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Unmatched users:</strong> {result.unmatched_users.join(', ')} — create matching accounts here (same email) before executing to keep their assignments intact.
                </div>
              </div>
            )}
            <ul className="text-[11px] text-slate-400 list-disc pl-4 space-y-0.5">
              {result.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
